import React, { useEffect, useRef, useState } from 'react';
import { AppState } from 'store/store';
import { useSelector } from 'react-redux';
import { formServices } from 'api/services';
import { IResForm } from 'api/services/forms/types';
import { Link } from 'react-router-dom';
import {
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  Table,
} from '@material-ui/core';
import { MainTemplate } from 'shared/MainTemplate/MainTemplate';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import EditIcon from '@material-ui/icons/Edit';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import Confirmation from 'shared/Confirmation/Confirmation';
import CustomModal from 'shared/CustomModal/CustomModal';
import Form from 'shared/Form/Form';
import { fields, view } from 'pages/FormPage/formData';
import handleNotification from 'helpers/handleNotification';

export const FormsPage: React.FC = () => {
  const { user } = useSelector((state: AppState) => state.userReducer);
  const { currentLang } = useSelector((state: AppState) => state.langReducer);
  const [data, setData] = useState<IResForm[]>();
  const [loading, setLoading] = useState(false);
  const [activeForm, setActiveForm] = useState<IResForm>();
  const createModalRef = useRef<any>();
  const createFormRef = useRef<any>();
  const updateModalRef = useRef<any>();
  const updateFormRef = useRef<any>();
  const ref = useRef<any>();

  useEffect(() => {
    if (currentLang?.code) getData();
  }, [currentLang?.code]);

  const getData = async () => {
    const res = await formServices.getForms().then((res) => res.data);
    setData(res);
  };

  const openUpdateModal = (item: IResForm) => {
    setActiveForm(item);
    updateModalRef.current.onShow();
  };

  const openCreateModal = () => {
    createModalRef.current.onShow();
  };

  const openDeleteModal = (item: IResForm) => {
    setActiveForm(item);
    ref.current.onShow();
  };

  const createForm = async () => {
    setLoading(true);
    const formRes = createFormRef.current?.submit();
    if (!formRes || formRes?.hasErrors) {
      handleNotification('Заполните поля');
      return;
    }

    const requestData = {
      email: formRes.res.email,
      name: formRes.res.name,
      title: formRes.res.title,
    };
    try {
      await formServices.creeateForm(requestData);
      getData();
      createModalRef.current.onHide();
    } finally {
      setLoading(false);
    }
  };

  const updateForm = async () => {
    if (!activeForm) return;

    const formRes = createFormRef.current?.submit();
    if (!formRes || formRes?.hasErrors) {
      handleNotification('Заполните поля');
      return;
    }

    const requestData = {
      email: formRes.res.email,
      name: formRes.res.name,
      title: formRes.res.title,
    };
    try {
      setLoading(true);
      await formServices.updateForm(activeForm.id, requestData);
      updateFormRef.current.onHide();
      getData();
    } finally {
      setLoading(false);
    }
  };

  const deleteForm = async () => {
    if (!activeForm) return;

    try {
      setLoading(true);
      await formServices.deleteForm(activeForm.id);
      setActiveForm(undefined);
      getData();
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainTemplate title="Формы" handler={openCreateModal} hideBtnByRole>
      <div className="forms-page">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Описание</TableCell>
              <TableCell>Почта</TableCell>
              <TableCell className="table-last-item">Действие</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((i) => (
              <TableRow key={i.id}>
                <TableCell>{i.title}</TableCell>
                <TableCell>{i.name}</TableCell>
                <TableCell>{i.email}</TableCell>
                <TableCell className="table-last-item">
                  <Link to={`forms/${i.id}`} className="table-icon">
                    <ArrowForwardIcon className="table-icon table-icon--black" />
                  </Link>
                  {user?.role === 'SU' && (
                    <>
                      <EditIcon
                        className="table-icon"
                        onClick={() => openUpdateModal(i)}
                      />
                      <DeleteForeverIcon
                        color="secondary"
                        onClick={() => openDeleteModal(i)}
                        className="table-icon"
                      />
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Confirmation
        ref={ref}
        text="Вы действительно хотите удалить форму?"
        submit={deleteForm}
        title="Удалить форму"
        load={loading}
        changeState={() => setActiveForm(undefined)}
      />
      <CustomModal
        ref={createModalRef}
        submit={createForm}
        title="Создание формы"
        text="Создать"
        load={loading}
        changeState={() => setActiveForm(undefined)}
      >
        <Form ref={createFormRef} view={view} fields={fields} />
      </CustomModal>
      <CustomModal
        ref={updateModalRef}
        submit={updateForm}
        title="Редактирование формы"
        text="Обновить"
        load={loading}
        changeState={() => setActiveForm(undefined)}
      >
        <Form
          ref={updateFormRef}
          view={view}
          fields={fields}
          initialData={activeForm}
        />
      </CustomModal>
    </MainTemplate>
  );
};
